import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    Paper,
    Divider,
    TextField,
    Button,
    Stack,
    Grid,
    InputAdornment,
    IconButton,
    FormControlLabel,
    Switch,
    TextareaAutosize,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/Visibility';


// --- Definición de los campos basada en la tabla (sin cambios) ---
const formFieldsDefinition = {
    wifi: {
        hab: { label: "Habilitar WiFi", type: "boolean", jsonKey: "hab", defaultValue: 1, min: 0, max: 1 },
        ssid: { label: "SSID", type: "text", jsonKey: "ssid", defaultValue: "ssid", minLength: 2, maxLength: 31 },
        pass: { label: "Contraseña", type: "password", jsonKey: "pass", defaultValue: "123456", minLength: 2, maxLength: 31 },
    },
    gprs: {
        hab: { label: "Habilitar GPRS", type: "boolean", jsonKey: "hab", defaultValue: 1, min: 0, max: 1 },
        apn: { label: "APN", type: "text", jsonKey: "apn", defaultValue: "internet.tigo.bo", minLength: 2, maxLength: 31 },
    },
    http: {
        hab: { label: "Habilitar HTTP", type: "boolean", jsonKey: "hab", defaultValue: 1, min: 0, max: 1 },
        url: { label: "URL", type: "text", jsonKey: "url", defaultValue: "alarmasCerberus.com", minLength: 8, maxLength: 63 },
        port: { label: "Puerto", type: "number", jsonKey: "port", defaultValue: 80, min: 1, max: 65536 },
    },
    mqtt: {
        broker: { label: "Broker", type: "text", jsonKey: "broker", defaultValue: "mosquitto.org", minLength: 8, maxLength: 63 },
        port: { label: "Puerto", type: "number", jsonKey: "port", defaultValue: 1883, min: 1, max: 65536 },
        user: { label: "Usuario", type: "text", jsonKey: "user", defaultValue: "admin", minLength: 2, maxLength: 31 },
        pass: { label: "Contraseña", type: "password", jsonKey: "pass", defaultValue: "123456", minLength: 2, maxLength: 31 },
        topicDev: { label: "Topic Dispositivo", type: "text", jsonKey: "topicDev", defaultValue: "ce/00010001", minLength: 2, maxLength: 31 },
        topicServer: { label: "Topic Servidor", type: "text", jsonKey: "topicServer", defaultValue: "ce/server", minLength: 2, maxLength: 31 },
        topicBcast: { label: "Topic Broadcast", type: "text", jsonKey: "topicBcast", defaultValue: "ce/bcast", minLength: 2, maxLength: 31 },
        qos: { label: "QoS", type: "number", jsonKey: "qos", defaultValue: 0, min: 0, max: 2 },
        kalive: { label: "KeepAlive", type: "number", jsonKey: "kalive", defaultValue: 30, min: 30, max: 900 },
    },
};

// --- Función auxiliar para inicializar el estado basado en la definición ---
const initializeConfigState = (initialData) => {
    const initialState = {};
    for (const groupKey in formFieldsDefinition) {
        initialState[groupKey] = {};
        for (const fieldKey in formFieldsDefinition[groupKey]) {
            const fieldDef = formFieldsDefinition[groupKey][fieldKey];
            initialState[groupKey][fieldDef.jsonKey] = initialData?.[groupKey]?.[fieldDef.jsonKey] ?? fieldDef.defaultValue;
        }
    }
    return initialState;
};

function NetworkConfigForm({ initialData, setInitialData }) {
    const initialConfig = initializeConfigState(initialData);
    const [networkConfig, setNetworkConfig] = useState(initialConfig);
    // originalConfig AHORA ES SOLO PARA LA LÓGICA DE CANCELAR Y EL ESTADO "DIRTY" VISUAL
    const [originalConfig, setOriginalConfig] = useState(initialConfig);

    const [jsonOutput, setJsonOutput] = useState('');
    const [showWifiPassword, setShowWifiPassword] = useState(false);
    const [showMqttPassword, setShowMqttPassword] = useState(false);
    // formDirty sigue indicando si hay cambios con respecto a originalConfig
    const [formDirty, setFormDirty] = useState(false);

    // --- useEffect para SINCRONIZAR CON EL PADRE y determinar si el formulario está dirty ---
    useEffect(() => {
        if (setInitialData) {
            setInitialData(networkConfig);
        }
        // formDirty: Indica si hay cambios con respecto a los datos ORIGINALES/ÚLTIMOS ENVIADOS.
        // Esto es para el botón CANCELAR y la advertencia visual.
        setFormDirty(JSON.stringify(networkConfig) !== JSON.stringify(originalConfig));
    }, [networkConfig, setInitialData, originalConfig]);

    // --- MANEJADORES DE CAMBIOS ---
    const handleChange = (groupKey, fieldKey) => (event) => {
        const fieldDef = formFieldsDefinition[groupKey][fieldKey];
        let value;

        if (fieldDef.type === "boolean") {
            value = event.target.checked ? 1 : 0;
        } else if (fieldDef.type === "number") {
            value = Number(event.target.value);
            if (isNaN(value)) value = fieldDef.defaultValue;
            if (fieldDef.min !== undefined && value < fieldDef.min) value = fieldDef.min;
            if (fieldDef.max !== undefined && value > fieldDef.max) value = fieldDef.max;
        } else {
            value = event.target.value;
        }

        setNetworkConfig(prevConfig => ({
            ...prevConfig,
            [groupKey]: {
                ...prevConfig[groupKey],
                [fieldDef.jsonKey]: value,
            },
        }));
    };

    const handleTogglePasswordVisibility = (field) => () => {
        if (field === 'wifi') {
            setShowWifiPassword(prev => !prev);
        } else if (field === 'mqtt') {
            setShowMqttPassword(prev => !prev);
        }
    };

    // --- Función para generar y mostrar el JSON ---
    const generateJson = () => {
        setJsonOutput(JSON.stringify(networkConfig, null, 2));
    };

    // --- Función para enviar la configuración ---
    const sendConfig = () => {
        //console.log("--- Enviando Configuración de Red JSON ---");
        //console.log(networkConfig);
        alert("Configuración de Red enviada (simulada). Revisa la consola.");

        // Si la configuración se envía exitosamente, los valores actuales del formulario
        // se convierten en los nuevos "originales" para futuras comparaciones de 'dirty'.
        setOriginalConfig(networkConfig);
        // Además, el JSON mostrado debe coincidir con los datos "enviados"
        setJsonOutput(JSON.stringify(networkConfig, null, 2));
    };

    // --- Función para manejar la cancelación ---
    const handleCancel = () => {
        if (formDirty) { // Solo muestra la alerta si hay cambios con respecto a originalConfig
            const confirmCancel = window.confirm(
                "¿Estás seguro de que quieres cancelar? Todos los cambios no guardados se perderán."
            );

            if (confirmCancel) {
                setNetworkConfig(originalConfig); // Restaura la configuración a los valores originales
                setJsonOutput(''); // Limpia el JSON de salida, ya que el JSON mostrado ahora es obsoleto
                alert("Cambios descartados. Formulario restaurado a los valores originales.");
            }
        } else {
            alert("No hay cambios que descartar.");
        }
    };

    // --- Función auxiliar para renderizar el resumen de valores (sin cambios) ---
    const renderSummary = () => {
        const summaryItems = [];
        for (const groupKey in networkConfig) {
            summaryItems.push(
                <Typography key={`group-${groupKey}`} variant="subtitle2" sx={{ mt: 1, mb: 0.5, textTransform: 'uppercase', color: 'primary.dark' }}>
                    {groupKey}
                </Typography>
            );
            for (const fieldJsonName in networkConfig[groupKey]) {
                const value = networkConfig[groupKey][fieldJsonName];
                let label = fieldJsonName;
                for (const fieldDefKey in formFieldsDefinition[groupKey]) {
                    if (formFieldsDefinition[groupKey][fieldDefKey].jsonKey === fieldJsonName) {
                        label = formFieldsDefinition[groupKey][fieldDefKey].label;
                        break;
                    }
                }

                let displayValue = value;
                if (typeof value === 'number' && (fieldJsonName === 'hab')) {
                    displayValue = value === 1 ? 'Habilitado' : value === 0 ? 'Deshabilitado' : value;
                }
                if (fieldJsonName.includes('pass')) {
                    displayValue = value ? '********' : 'Vacía';
                }
                if (fieldJsonName === 'qos') {
                    displayValue = `${value} (QoS)`;
                }
                if (fieldJsonName === 'kalive') {
                    displayValue = `${value} segundos`;
                }

                summaryItems.push(
                    <Typography key={`${groupKey}-${fieldJsonName}`} variant="body2" sx={{ ml: 2, fontFamily: 'monospace', mb: 0.5 }}>
                        - {label}: <strong>{displayValue}</strong>
                    </Typography>
                );
            }
        }
        return summaryItems;
    };

    // Calcular si el JSON mostrado necesita ser actualizado para COINCIDIR con networkConfig.
    // Esto es true si jsonOutput está vacío O no coincide con el networkConfig actual.
    const currentNetworkConfigString = JSON.stringify(networkConfig, null, 2);
    const jsonNeedsUpdate = jsonOutput === '' || (currentNetworkConfigString !== jsonOutput);

    // El botón ENVIAR se habilita SI Y SOLO SI el JSON mostrado está ACTUALIZADO
    // con los datos del formulario (networkConfig).
    const canSend = !jsonNeedsUpdate;


    return (
        <Container maxWidth="lg" sx={{ mt: 4 }}>
            <Paper elevation={3} sx={{ p: 3 }}>
                <Typography variant="h5" component="h1" gutterBottom>
                    Configuración de Conectividad
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={3}>

                    {/* Primera Columna: WiFi, GPRS, HTTP */}
                    <Grid item xs={12} md={4}>
                        <Stack spacing={3} sx={{ height: '100%' }}>
                            {/* Configuración WiFi */}
                            <Paper variant="outlined" sx={{ p: 2, position: 'relative', pt: 4, flexGrow: 1 }}>
                                <Typography
                                    variant="caption" component="div"
                                    sx={{ position: 'absolute', top: 0, left: 12, transform: 'translateY(-50%)', bgcolor: 'background.paper', px: 1, color: 'primary.main', fontWeight: 'bold', fontSize: '0.8rem', textTransform: 'uppercase' }}
                                >
                                    WiFi
                                </Typography>
                                <Box>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={networkConfig.wifi.hab === 1}
                                                onChange={handleChange('wifi', 'hab')}
                                                color="primary"
                                            />
                                        }
                                        label={formFieldsDefinition.wifi.hab.label}
                                        sx={{ mb: 1 }}
                                    />
                                    <TextField
                                        label={formFieldsDefinition.wifi.ssid.label}
                                        value={networkConfig.wifi.ssid}
                                        onChange={handleChange('wifi', 'ssid')}
                                        fullWidth
                                        margin="normal"
                                        inputProps={{
                                            minLength: formFieldsDefinition.wifi.ssid.minLength,
                                            maxLength: formFieldsDefinition.wifi.ssid.maxLength
                                        }}
                                    />
                                    <TextField
                                        label={formFieldsDefinition.wifi.pass.label}
                                        type={showWifiPassword ? 'text' : 'password'}
                                        value={networkConfig.wifi.pass}
                                        onChange={handleChange('wifi', 'pass')}
                                        fullWidth
                                        margin="normal"
                                        inputProps={{
                                            minLength: formFieldsDefinition.wifi.pass.minLength,
                                            maxLength: formFieldsDefinition.wifi.pass.maxLength
                                        }}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        aria-label="toggle password visibility"
                                                        onClick={handleTogglePasswordVisibility('wifi')}
                                                        edge="end"
                                                    >
                                                        {showWifiPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Box>
                            </Paper>
                            {/* Configuración GPRS */}
                            <Paper variant="outlined" sx={{ p: 2, position: 'relative', pt: 4, flexGrow: 1 }}>
                                <Typography
                                    variant="caption" component="div"
                                    sx={{ position: 'absolute', top: 0, left: 12, transform: 'translateY(-50%)', bgcolor: 'background.paper', px: 1, color: 'primary.main', fontWeight: 'bold', fontSize: '0.8rem', textTransform: 'uppercase' }}
                                >
                                    GPRS
                                </Typography>
                                <Box>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={networkConfig.gprs.hab === 1}
                                                onChange={handleChange('gprs', 'hab')}
                                                color="primary"
                                            />
                                        }
                                        label={formFieldsDefinition.gprs.hab.label}
                                        sx={{ mb: 1 }}
                                    />
                                    <TextField
                                        label={formFieldsDefinition.gprs.apn.label}
                                        value={networkConfig.gprs.apn}
                                        onChange={handleChange('gprs', 'apn')}
                                        fullWidth
                                        margin="normal"
                                        inputProps={{
                                            minLength: formFieldsDefinition.gprs.apn.minLength,
                                            maxLength: formFieldsDefinition.gprs.apn.maxLength
                                        }}
                                    />
                                </Box>
                            </Paper>
                            {/* Configuración HTTP */}
                            <Paper variant="outlined" sx={{ p: 2, position: 'relative', pt: 4, flexGrow: 1 }}>
                                <Typography
                                    variant="caption" component="div"
                                    sx={{ position: 'absolute', top: 0, left: 12, transform: 'translateY(-50%)', bgcolor: 'background.paper', px: 1, color: 'primary.main', fontWeight: 'bold', fontSize: '0.8rem', textTransform: 'uppercase' }}
                                >
                                    HTTP
                                </Typography>
                                <Box>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={networkConfig.http.hab === 1}
                                                onChange={handleChange('http', 'hab')}
                                                color="primary"
                                            />
                                        }
                                        label={formFieldsDefinition.http.hab.label}
                                        sx={{ mb: 1 }}
                                    />
                                    <TextField
                                        label={formFieldsDefinition.http.url.label}
                                        value={networkConfig.http.url}
                                        onChange={handleChange('http', 'url')}
                                        fullWidth
                                        margin="normal"
                                        inputProps={{
                                            minLength: formFieldsDefinition.http.url.minLength,
                                            maxLength: formFieldsDefinition.http.url.maxLength
                                        }}
                                    />
                                    <TextField
                                        label={formFieldsDefinition.http.port.label}
                                        type="number"
                                        value={networkConfig.http.port}
                                        onChange={handleChange('http', 'port')}
                                        fullWidth
                                        margin="normal"
                                        inputProps={{
                                            min: formFieldsDefinition.http.port.min,
                                            max: formFieldsDefinition.http.port.max
                                        }}
                                    />
                                </Box>
                            </Paper>
                        </Stack>
                    </Grid>

                    {/* Segunda Columna: SOLO MQTT */}
                    <Grid item xs={12} md={4}>
                        <Paper variant="outlined" sx={{ p: 2, height: '100%', position: 'relative', pt: 4 }}>
                            <Typography
                                variant="caption" component="div"
                                sx={{ position: 'absolute', top: 0, left: 12, transform: 'translateY(-50%)', bgcolor: 'background.paper', px: 1, color: 'primary.main', fontWeight: 'bold', fontSize: '0.8rem', textTransform: 'uppercase' }}
                            >
                                MQTT
                            </Typography>
                            <Box> {/* No usar Grid interno aquí, solo Box para apilar verticalmente */}
                                <TextField
                                    label={formFieldsDefinition.mqtt.broker.label}
                                    value={networkConfig.mqtt.broker}
                                    onChange={handleChange('mqtt', 'broker')}
                                    fullWidth
                                    margin="normal"
                                    inputProps={{
                                        minLength: formFieldsDefinition.mqtt.broker.minLength,
                                        maxLength: formFieldsDefinition.mqtt.broker.maxLength
                                    }}
                                />
                                <TextField
                                    label={formFieldsDefinition.mqtt.port.label}
                                    type="number"
                                    value={networkConfig.mqtt.port}
                                    onChange={handleChange('mqtt', 'port')}
                                    fullWidth
                                    margin="normal"
                                    inputProps={{
                                        min: formFieldsDefinition.mqtt.port.min,
                                        max: formFieldsDefinition.mqtt.port.max
                                    }}
                                />
                                <TextField
                                    label={formFieldsDefinition.mqtt.user.label}
                                    value={networkConfig.mqtt.user}
                                    onChange={handleChange('mqtt', 'user')}
                                    fullWidth
                                    margin="normal"
                                    inputProps={{
                                        minLength: formFieldsDefinition.mqtt.user.minLength,
                                        maxLength: formFieldsDefinition.mqtt.user.maxLength
                                    }}
                                />
                                <TextField
                                    label={formFieldsDefinition.mqtt.pass.label}
                                    type={showMqttPassword ? 'text' : 'password'}
                                    value={networkConfig.mqtt.pass}
                                    onChange={handleChange('mqtt', 'pass')}
                                    fullWidth
                                    margin="normal"
                                    inputProps={{
                                        minLength: formFieldsDefinition.mqtt.pass.minLength,
                                        maxLength: formFieldsDefinition.mqtt.pass.maxLength
                                    }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={handleTogglePasswordVisibility('mqtt')}
                                                    edge="end"
                                                >
                                                    {showMqttPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <TextField
                                    label={formFieldsDefinition.mqtt.topicDev.label}
                                    value={networkConfig.mqtt.topicDev}
                                    onChange={handleChange('mqtt', 'topicDev')}
                                    fullWidth
                                    margin="normal"
                                    inputProps={{
                                        minLength: formFieldsDefinition.mqtt.topicDev.minLength,
                                        maxLength: formFieldsDefinition.mqtt.topicDev.maxLength
                                    }}
                                />
                                <TextField
                                    label={formFieldsDefinition.mqtt.topicServer.label}
                                    value={networkConfig.mqtt.topicServer}
                                    onChange={handleChange('mqtt', 'topicServer')}
                                    fullWidth
                                    margin="normal"
                                    inputProps={{
                                        minLength: formFieldsDefinition.mqtt.topicServer.minLength,
                                        maxLength: formFieldsDefinition.mqtt.topicServer.maxLength
                                    }}
                                />
                                <TextField
                                    label={formFieldsDefinition.mqtt.topicBcast.label}
                                    value={networkConfig.mqtt.topicBcast}
                                    onChange={handleChange('mqtt', 'topicBcast')}
                                    fullWidth
                                    margin="normal"
                                    inputProps={{
                                        minLength: formFieldsDefinition.mqtt.topicBcast.minLength,
                                        maxLength: formFieldsDefinition.mqtt.topicBcast.maxLength
                                    }}
                                />
                                <TextField
                                    label={formFieldsDefinition.mqtt.qos.label}
                                    type="number"
                                    value={networkConfig.mqtt.qos}
                                    onChange={handleChange('mqtt', 'qos')}
                                    fullWidth
                                    margin="normal"
                                    inputProps={{
                                        min: formFieldsDefinition.mqtt.qos.min,
                                        max: formFieldsDefinition.mqtt.qos.max
                                    }}
                                />
                                <TextField
                                    label={formFieldsDefinition.mqtt.kalive.label}
                                    type="number"
                                    value={networkConfig.mqtt.kalive}
                                    onChange={handleChange('mqtt', 'kalive')}
                                    fullWidth
                                    margin="normal"
                                    inputProps={{
                                        min: formFieldsDefinition.mqtt.kalive.min,
                                        max: formFieldsDefinition.mqtt.kalive.max
                                    }}
                                />
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Tercera Columna: JSON de Configuración y Resumen de Valores */}
                    <Grid item xs={12} md={4}>
                        <Stack spacing={3} sx={{ height: '100%' }}>
                            {/* JSON de Configuración */}
                            <Paper variant="outlined" sx={{ p: 2, position: 'relative', pt: 4, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                                <Typography
                                    variant="caption" component="div"
                                    sx={{ position: 'absolute', top: 0, left: 12, transform: 'translateY(-50%)', bgcolor: 'background.paper', px: 1, color: 'primary.main', fontWeight: 'bold', fontSize: '0.8rem', textTransform: 'uppercase' }}
                                >
                                    JSON de Configuración
                                </Typography>
                                {jsonOutput ? (
                                    <Box sx={{ flexGrow: 1, overflowY: 'auto', mb: 2 }}>
                                        <TextareaAutosize
                                            value={jsonOutput}
                                            minRows={10}
                                            maxRows={20}
                                            style={{
                                                width: '100%',
                                                border: '1px solid #ccc',
                                                borderRadius: '4px',
                                                padding: '8px',
                                                fontFamily: 'monospace',
                                                resize: 'vertical',
                                                backgroundColor: jsonNeedsUpdate ? '#ffe0b2' : 'white',
                                                borderColor: jsonNeedsUpdate ? '#ff9800' : '#ccc',
                                            }}
                                            readOnly
                                        />
                                    </Box>
                                ) : (
                                    <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Typography color="text.secondary">
                                            Genera el JSON para previsualizarlo aquí.
                                        </Typography>
                                    </Box>
                                )}
                                <Button
                                    variant="contained"
                                    color={jsonNeedsUpdate ? "warning" : "primary"}
                                    onClick={generateJson}
                                    sx={{ mt: 'auto' }}
                                    startIcon={jsonNeedsUpdate ? <i className="fa-solid fa-triangle-exclamation"></i> : null}
                                >
                                    {jsonNeedsUpdate ? "Actualizar JSON" : "Generar JSON"}
                                </Button>
                            </Paper>

                            {/* Resumen de Valores */}
                            <Paper variant="outlined" sx={{ p: 2, position: 'relative', pt: 4, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                                <Typography
                                    variant="caption" component="div"
                                    sx={{ position: 'absolute', top: 0, left: 12, transform: 'translateY(-50%)', bgcolor: 'background.paper', px: 1, color: 'primary.main', fontWeight: 'bold', fontSize: '0.8rem', textTransform: 'uppercase' }}
                                >
                                    Resumen de Valores
                                </Typography>
                                <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
                                    {renderSummary()}
                                </Box>
                            </Paper>
                        </Stack>
                    </Grid>

                    {/* Fila de Botones de acción general (CANCELAR, ENVIAR) */}
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 1 }}>
                            <Stack direction="row" spacing={2}>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    onClick={handleCancel}
                                    startIcon={<i className="fa-solid fa-xmark"></i>}
                                >
                                    CANCELAR
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={sendConfig}
                                    startIcon={<i className="fa-solid fa-paper-plane"></i>}
                                    // El botón ENVIAR se habilita SI Y SOLO SI el JSON mostrado está actualizado
                                    // con los datos del formulario (networkConfig).
                                    disabled={jsonNeedsUpdate}
                                >
                                    ENVIAR
                                </Button>
                            </Stack>
                        </Box>
                    </Grid>

                </Grid>

                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
                    * Configuración de los parámetros de conectividad de red.
                </Typography>
            </Paper>
        </Container>
    );
}

export default NetworkConfigForm;